import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import moment from "moment";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import {
  FaArrowRight,
  FaChartBar,
  FaClock,
  FaDownload,
  FaHeadset,
  FaLock,
  FaReceipt,
  FaSignOutAlt,
  FaShieldAlt,
  FaWallet,
} from "react-icons/fa";

import FuelFlowLogo from "./CSS/fuelFlowLogo.png";
import PampaChatWidget from "./PampaChatWidget";
import "./CSS/CustomerPortal.css";

const tabs = [
  { key: "overview", label: "Overview", icon: FaWallet },
  { key: "statement", label: "Statement", icon: FaReceipt },
  { key: "reports", label: "Reports", icon: FaChartBar },
  { key: "support", label: "Support", icon: FaHeadset },
  { key: "security", label: "Security", icon: FaLock },
];

const readStoredValue = (key, fallback = "") => {
  const raw = localStorage.getItem(key);
  if (raw == null) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed || fallback;
  } catch (err) {
    return String(raw).replace(/^["']+|["']+$/g, "").trim() || fallback;
  }
};

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) {
    const joined = value.map((item) => safeText(item, "")).filter(Boolean).join(" | ");
    return joined || fallback;
  }
  if (typeof value === "object") return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const money = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

const dt = (value) =>
  value ? moment(value).format("DD-MM-YYYY hh:mm A") : "-";

export default function CustomerPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const role = String(readStoredValue("role", "")).toLowerCase();

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassbook, setLoadingPassbook] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedAccountId, setSelectedAccountId] = useState("ALL");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DDTHH:mm"));
  const [endDate, setEndDate] = useState(moment().endOf("month").format("YYYY-MM-DDTHH:mm"));
  const [openingBalance, setOpeningBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    total_debit: 0,
    total_credit: 0,
    closing_balance: 0,
  });
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    title: "",
    description: "",
    priority: "medium",
  });
  const [ticketReply, setTicketReply] = useState("");
  const [savingTicket, setSavingTicket] = useState(false);
  const [savingTicketReply, setSavingTicketReply] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [notice, setNotice] = useState(null);

  const customerName = safeText(profile?.customer?.name, readStoredValue("name", "Customer"));

  const accounts = profile?.accounts || [];
  const selectedAccount = useMemo(() => {
    if (!accounts.length) return null;
    if (selectedAccountId === "ALL") return null;
    return accounts.find((row) => String(row.tenant_id) === String(selectedAccountId)) || null;
  }, [accounts, selectedAccountId]);

  const visibleTransactions = useMemo(() => {
    return Array.isArray(transactions) ? transactions : [];
  }, [transactions]);

  const paymentSummary = useMemo(() => {
    const totalDebit = Number(summary.total_debit || 0);
    const totalCredit = Number(summary.total_credit || 0);
    const closingBalance = Number(summary.closing_balance || 0);
    const dueShare = Math.max(closingBalance, 0);
    const paidShare = Math.max(totalDebit - dueShare, 0);

    return {
      dueShare,
      paidShare,
      totalDebit,
      totalCredit,
      closingBalance,
    };
  }, [summary]);

  useEffect(() => {
    if (!token || role !== "customer") {
      navigate("/");
    }
  }, [navigate, role, token]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 4200);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const maybeTab = parts[1];
    if (tabs.some((tab) => tab.key === maybeTab)) {
      setActiveTab(maybeTab);
    } else {
      setActiveTab("overview");
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (!profile) return;
    fetchPassbook();
  }, [profile, selectedAccountId, startDate, endDate]);

  useEffect(() => {
    if (!profile) return;
    fetchSupportTickets();
  }, [profile]);

  useEffect(() => {
    if (activeTab !== "support" || !selectedTicketId) return;
    fetchSupportTicketDetail(selectedTicketId);
  }, [activeTab, selectedTicketId]);

  const showNotice = (type, title, message) => {
    setNotice({ id: Date.now(), type, title, message });
  };

  const routeToTab = (key) => {
    const nextPath = key === "overview" ? "/customer" : `/customer/${key}`;
    navigate(nextPath);
  };

  const assetToDataUrl = async (assetUrl) => {
    try {
      const res = await fetch(assetUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      return null;
    }
  };

  const headerLine = useMemo(() => {
    const accountLabel = selectedAccount?.bunk_name || "All linked bunks";
    return `${accountLabel}`;
  }, [selectedAccount]);

  const fetchProfile = async () => {
    setLoadingProfile(true);
    try {
      const res = await api.get("/api/customer-portal/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || null;
      setProfile(data);

      const nextAccounts = Array.isArray(data?.accounts) ? data.accounts : [];
      if (nextAccounts.length === 1) {
        setSelectedAccountId(String(nextAccounts[0].tenant_id));
      } else if (nextAccounts.length > 1 && selectedAccountId === null) {
        setSelectedAccountId("ALL");
      } else if (!selectedAccountId) {
        setSelectedAccountId("ALL");
      }
    } catch (err) {
      showNotice("error", "Unable to load account", err?.response?.data?.error || "We could not load your profile.");
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchPassbook = async () => {
    if (!profile) return;
    setLoadingPassbook(true);
    try {
      const params = {
        startDateTime: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
        endDateTime: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
      };
      if (selectedAccountId !== "ALL") {
        params.tenantId = selectedAccountId;
      } else {
        params.tenantId = "all";
      }

      const res = await api.get("/api/customer-portal/passbook", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setOpeningBalance(Number(res.data?.opening_balance || 0));
      setTransactions(Array.isArray(res.data?.transactions) ? res.data.transactions : []);
      setSummary(res.data?.summary || { total_debit: 0, total_credit: 0, closing_balance: 0 });
    } catch (err) {
      showNotice(
        "error",
        "Statement not ready",
        err?.response?.data?.error || "We could not fetch your statement right now."
      );
    } finally {
      setLoadingPassbook(false);
    }
  };

  const fetchSupportTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await api.get("/api/support-tickets", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setTickets(rows);
      if (!selectedTicketId && rows.length) {
        setSelectedTicketId(rows[0].id);
      } else if (selectedTicketId && !rows.some((row) => String(row.id) === String(selectedTicketId))) {
        setSelectedTicketId(rows[0]?.id || null);
      }
    } catch (err) {
      showNotice(
        "error",
        "Support cases unavailable",
        err?.response?.data?.error || "We could not load your support cases right now."
      );
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchSupportTicketDetail = async (ticketId) => {
    if (!ticketId) {
      setSelectedTicketDetail(null);
      return;
    }

    try {
      const res = await api.get(`/api/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedTicketDetail(res.data || null);
    } catch (err) {
      showNotice(
        "error",
        "Support case unavailable",
        err?.response?.data?.error || "We could not load this support case."
      );
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.title.trim() || !ticketForm.description.trim()) {
      showNotice("error", "Issue details missing", "Please enter both a title and issue description.");
      return;
    }

    setSavingTicket(true);
    try {
      const res = await api.post(
        "/api/support-tickets",
        {
          title: ticketForm.title.trim(),
          description: ticketForm.description.trim(),
          priority: ticketForm.priority,
          source: "portal",
          channel: "web",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTicketForm({ title: "", description: "", priority: "medium" });
      showNotice("success", "Support case created", `Ticket ${res.data?.ticket_number || ""} is now in the queue.`.trim());
      await fetchSupportTickets();
      if (res.data?.id) {
        setSelectedTicketId(res.data.id);
        await fetchSupportTicketDetail(res.data.id);
      }
      routeToTab("support");
    } catch (err) {
      showNotice(
        "error",
        "Could not create support case",
        err?.response?.data?.error || "We could not create the support case right now."
      );
    } finally {
      setSavingTicket(false);
    }
  };

  const sendTicketReply = async (e) => {
    e.preventDefault();
    if (!selectedTicketId || !ticketReply.trim()) {
      return;
    }

    setSavingTicketReply(true);
    try {
      await api.post(
        `/api/support-tickets/${selectedTicketId}/replies`,
        { message: ticketReply.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicketReply("");
      await fetchSupportTickets();
      await fetchSupportTicketDetail(selectedTicketId);
      showNotice("success", "Reply sent", "Your update has been added to the support case.");
    } catch (err) {
      showNotice(
        "error",
        "Could not send reply",
        err?.response?.data?.error || "We could not add your reply right now."
      );
    } finally {
      setSavingTicketReply(false);
    }
  };

  const saveExcel = async () => {
    if (!visibleTransactions.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Statement");
    const accountLabel = selectedAccount?.bunk_name || "All linked bunks";
    const rows = visibleTransactions.map((row, index) => [
      index + 1,
      dt(row.transaction_datetime),
      safeText(row.bunk_name),
      safeText(row.vehicle_no),
      safeText(row.receipt_or_indent_no),
      Number(row.debit || 0).toFixed(2),
      Number(row.credit || 0).toFixed(2),
      Number(row.balance || 0).toFixed(2),
      safeText(row.comment),
    ]);

    const fuelLogo = await assetToDataUrl(FuelFlowLogo);

    worksheet.mergeCells("A1:I1");
    worksheet.getCell("A1").value = customerName;
    worksheet.mergeCells("A2:I2");
    worksheet.getCell("A2").value = "Customer Statement";
    worksheet.mergeCells("A3:I3");
    worksheet.getCell("A3").value = headerLine;
    worksheet.mergeCells("A4:I4");
    worksheet.getCell("A4").value = `Customer Statement - ${accountLabel}`;
    worksheet.mergeCells("A5:I5");
    worksheet.getCell("A5").value = `Period: ${moment(startDate).format("DD-MM-YYYY HH:mm")} to ${moment(endDate).format("DD-MM-YYYY HH:mm")}`;
    worksheet.mergeCells("A6:I6");
    worksheet.getCell("A6").value = `Opening Balance: ${money(openingBalance)}`;

    [1, 2, 3, 4, 5, 6].forEach((rowNo) => {
      const row = worksheet.getRow(rowNo);
      row.font = { bold: rowNo <= 4, size: rowNo <= 4 ? 12 : 10 };
      row.alignment = { vertical: "middle", horizontal: "left" };
    });

    if (fuelLogo) {
      const img = fuelLogo;
      worksheet.addImage(workbook.addImage({ base64: img.split(",")[1], extension: "png" }), "J1:K2");
    }
    const headerRow = worksheet.addRow([
      "#",
      "Date & Time",
      "Bunk",
      "Vehicle No",
      "Receipt / Indent No",
      "Debit",
      "Credit",
      "Balance",
      "Comment",
    ]);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    rows.forEach((row) => worksheet.addRow(row));

    worksheet.columns = [
      { width: 6 },
      { width: 20 },
      { width: 18 },
      { width: 18 },
      { width: 18 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 30 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FuelFlow_Customer_Statement_${moment(startDate).format("DDMMYYYY_HHmm")}_${moment(endDate).format("DDMMYYYY_HHmm")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const savePdf = async () => {
    if (!visibleTransactions.length) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const width = doc.internal.pageSize.getWidth();
    const accountLabel = selectedAccount?.bunk_name || "All linked bunks";
    const fuelLogo = await assetToDataUrl(FuelFlowLogo);

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, width, 34, "F");
    if (fuelLogo) doc.addImage(fuelLogo, "PNG", 10, 7, 18, 18);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(customerName, 32, 13);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Customer Statement", 32, 19);
    doc.text(headerLine, 32, 25);
    doc.text(`Customer Statement - ${accountLabel}`, 32, 31);

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Opening Balance: ${money(openingBalance)}`, 12, 43);
    doc.text(
      `Period: ${moment(startDate).format("DD-MM-YYYY HH:mm")} to ${moment(endDate).format("DD-MM-YYYY HH:mm")}`,
      12,
      49
    );

    const body = visibleTransactions.map((row, index) => [
      index + 1,
      dt(row.transaction_datetime),
      safeText(row.bunk_name),
      safeText(row.vehicle_no),
      safeText(row.receipt_or_indent_no),
      Number(row.debit || 0).toFixed(2),
      Number(row.credit || 0).toFixed(2),
      Number(row.balance || 0).toFixed(2),
      safeText(row.comment),
    ]);

    autoTable(doc, {
      startY: 54,
      head: [[
        "#",
        "Date & Time",
        "Bunk",
        "Vehicle No",
        "Receipt / Indent No",
        "Debit",
        "Credit",
        "Balance",
        "Comment",
      ]],
      body,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineColor: [15, 23, 42],
        lineWidth: 0.35,
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        lineColor: [15, 23, 42],
        lineWidth: 0.45,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      tableLineColor: [15, 23, 42],
      tableLineWidth: 0.5,
    });

    doc.save(`FuelFlow_Customer_Statement_${moment(startDate).format("DDMMYYYY_HHmm")}_${moment(endDate).format("DDMMYYYY_HHmm")}.pdf`);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      showNotice("error", "Missing password", "Please fill current and new password.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotice("error", "Password mismatch", "New password and confirm password must match.");
      return;
    }

    setSavingPassword(true);
    try {
      await api.post(
        "/api/customer-portal/change-password",
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      showNotice("success", "Password updated", "Your password has been updated.");
    } catch (err) {
      showNotice(
        "error",
        "Could not update password",
        err?.response?.data?.error || "We could not change your password right now."
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const overviewChart = useMemo(() => {
    const due = Number(profile?.totals?.outstanding || 0);
    const paid = Number(profile?.totals?.total_paid || 0);
    return {
      labels: ["Settled", "Outstanding"],
      datasets: [
        {
          data: [Math.max(paid, 0), Math.max(due, 0)],
          backgroundColor: ["#16a34a", "#2563eb"],
          borderWidth: 0,
        },
      ],
    };
  }, [profile]);

  const accountChart = useMemo(() => {
    const labels = accounts.map((row) => safeText(row.bunk_name, "Bunk"));
    const values = accounts.map((row) => Number(row.outstanding || 0));
    return {
      labels: labels.length ? labels : ["No accounts"],
      datasets: [
        {
          label: "Outstanding",
          data: values.length ? values : [0],
          backgroundColor: "#f59e0b",
          borderRadius: 10,
        },
      ],
    };
  }, [accounts]);

  const recentCountsChart = useMemo(() => {
    const paymentRows = visibleTransactions.filter((row) => Number(row.credit || 0) > 0);
    const fuelRows = visibleTransactions.filter((row) => Number(row.debit || 0) > 0);
    return {
      labels: ["Fuel credit", "Payments"],
      datasets: [
        {
          data: [fuelRows.length, paymentRows.length],
          backgroundColor: ["#dc2626", "#16a34a"],
          borderRadius: 10,
        },
      ],
    };
  }, [visibleTransactions]);

  return (
    <div className="customer-portal">
      <div className="customer-portal__hero">
        <div className="customer-portal__brand">
          <div>
            <div className="customer-portal__eyebrow">Customer Portal</div>
            <h1>{customerName}</h1>
            <p>Account overview</p>
          </div>
        </div>

        <div className="customer-portal__actions">
          <button className="customer-portal__ghost" type="button" onClick={() => routeToTab("statement")}>
            <FaReceipt /> Statement
          </button>
          <button className="customer-portal__ghost" type="button" onClick={() => routeToTab("security")}>
            <FaShieldAlt /> Security
          </button>
          <button className="customer-portal__danger" type="button" onClick={() => {
            localStorage.clear();
            navigate("/");
          }}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </div>

      <div className="customer-portal__tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              className={`customer-portal__tab ${activeTab === tab.key ? "is-active" : ""}`}
              onClick={() => routeToTab(tab.key)}
            >
              <Icon />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="customer-portal__toolbar">
        <div className="customer-portal__filter">
          <label>Associated bunk</label>
          <select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!accounts.length || loadingProfile}
          >
            <option value="ALL">All associated bunks</option>
            {accounts.map((row) => (
              <option key={row.tenant_id} value={row.tenant_id}>
                {safeText(row.bunk_name)}
              </option>
            ))}
          </select>
        </div>
        <div className="customer-portal__filter">
          <label>Start date & time</label>
          <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="customer-portal__filter">
          <label>End date & time</label>
          <input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <button
          className="customer-portal__primary"
          type="button"
          onClick={fetchPassbook}
          disabled={loadingPassbook || loadingProfile}
        >
          <FaArrowRight /> Refresh
        </button>
      </div>

      {notice && (
        <div className={`customer-portal__notice ${notice.type}`}>
          <strong>{notice.title}</strong>
          <span>{notice.message}</span>
        </div>
      )}

      <div className="customer-portal__stats">
        <div className="customer-portal__stat">
          <span>Outstanding</span>
          <strong>{money(profile?.totals?.outstanding || 0)}</strong>
        </div>
        <div className="customer-portal__stat">
          <span>Settled</span>
          <strong>{money(profile?.totals?.total_paid || 0)}</strong>
        </div>
        <div className="customer-portal__stat">
          <span>Billed</span>
          <strong>{money(profile?.totals?.total_credit || 0)}</strong>
        </div>
        <div className="customer-portal__stat">
          <span>Linked bunks</span>
          <strong>{profile?.totals?.account_count || 0}</strong>
        </div>
      </div>

      <div className="customer-portal__content">
        {activeTab === "overview" && (
          <div className="customer-portal__grid">
            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Balance snapshot</p>
                  <h2>Where things stand right now</h2>
                </div>
                <div className="customer-portal__chip">
                  <FaClock /> {moment().format("DD MMM, hh:mm A")}
                </div>
              </div>
              <div className="customer-portal__summary-row">
                <div className="customer-portal__summary-card credit">
                  <span>Total billed</span>
                  <strong>{money(profile?.totals?.total_credit || 0)}</strong>
                </div>
                <div className="customer-portal__summary-card paid">
                  <span>Total settled</span>
                  <strong>{money(profile?.totals?.total_paid || 0)}</strong>
                </div>
                <div className="customer-portal__summary-card due">
                  <span>Outstanding</span>
                  <strong>{money(profile?.totals?.outstanding || 0)}</strong>
                </div>
              </div>
              <div className="customer-portal__chart-box">
                <Doughnut data={overviewChart} />
              </div>
            </section>

            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Linked bunks</p>
                  <h2>Your associated stations</h2>
                </div>
              </div>
              <div className="customer-portal__account-list">
                {(accounts || []).map((row) => (
                  <article key={row.tenant_id} className="customer-portal__account-card">
                    <div>
                      <strong>{safeText(row.bunk_name, "Bunk")}</strong>
                    </div>
                    <div className="customer-portal__account-meta">
                      <b>{money(row.outstanding || 0)}</b>
                    </div>
                  </article>
                ))}
                {!accounts.length && (
                  <div className="customer-portal__empty">
                    We are still loading your linked account details.
                  </div>
                )}
              </div>
            </section>

            <section className="customer-portal__panel customer-portal__panel--wide">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Distribution</p>
                  <h2>Outstanding by bunk</h2>
                </div>
              </div>
              <div className="customer-portal__chart-box">
                <Bar data={accountChart} />
              </div>
            </section>
          </div>
        )}

        {activeTab === "statement" && (
          <section className="customer-portal__panel customer-portal__panel--full">
            <div className="customer-portal__panel-head customer-portal__panel-head--wrap">
              <div>
                <p className="customer-portal__kicker">Statement</p>
                <h2>Fuel, payments and current balance</h2>
                <p className="customer-portal__muted">
                  The statement can be filtered by date and time. Credit settlements are consolidated by minute.
                </p>
              </div>
              <div className="customer-portal__download-actions">
                <button className="customer-portal__ghost" type="button" onClick={saveExcel} disabled={!visibleTransactions.length}>
                  <FaDownload /> Excel
                </button>
                <button className="customer-portal__ghost" type="button" onClick={savePdf} disabled={!visibleTransactions.length}>
                  <FaDownload /> PDF
                </button>
              </div>
            </div>

            <div className="customer-portal__statement-top">
              <div className="customer-portal__statement-card">
                <span>Opening balance</span>
                <strong>{money(openingBalance)}</strong>
              </div>
              <div className="customer-portal__statement-card credit">
                <span>Credit given</span>
                <strong>{money(summary.total_debit || 0)}</strong>
              </div>
              <div className="customer-portal__statement-card paid">
                <span>Payments received</span>
                <strong>{money(summary.total_credit || 0)}</strong>
              </div>
              <div className="customer-portal__statement-card due">
                <span>Closing balance</span>
                <strong>{money(summary.closing_balance || 0)}</strong>
              </div>
            </div>

            <div className="customer-portal__table-wrap">
              <table className="customer-portal__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date & Time</th>
                    <th>Bunk</th>
                    <th>Vehicle No</th>
                    <th>Receipt / Indent No</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPassbook ? (
                    <tr>
                      <td colSpan={9} className="customer-portal__table-empty">
                        Loading your statement...
                      </td>
                    </tr>
                  ) : visibleTransactions.length ? (
                    visibleTransactions.map((row, index) => (
                      <tr key={`${row.source_type || "row"}-${row.source_id || index}`}>
                        <td>{index + 1}</td>
                        <td>{dt(row.transaction_datetime)}</td>
                        <td>{safeText(row.bunk_name)}</td>
                        <td>{safeText(row.vehicle_no)}</td>
                        <td>{safeText(row.receipt_or_indent_no)}</td>
                        <td className="neg">{Number(row.debit || 0).toFixed(2)}</td>
                        <td className="pos">{Number(row.credit || 0).toFixed(2)}</td>
                        <td className="bal">{Number(row.balance || 0).toFixed(2)}</td>
                        <td>{safeText(row.comment)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="customer-portal__table-empty">
                        No statement entries found for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === "reports" && (
          <div className="customer-portal__grid">
            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Reports</p>
                  <h2>Payment mix</h2>
                </div>
              </div>
              <div className="customer-portal__chart-box">
                <Doughnut data={overviewChart} />
              </div>
            </section>

            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Activity</p>
                  <h2>Fuel credits vs settlements</h2>
                </div>
              </div>
              <div className="customer-portal__chart-box">
                <Bar data={recentCountsChart} />
              </div>
            </section>

            <section className="customer-portal__panel customer-portal__panel--wide">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Latest statement lines</p>
                  <h2>Recent movements</h2>
                </div>
              </div>
              <div className="customer-portal__mini-list">
                {(visibleTransactions || []).slice(0, 8).map((row, index) => (
                  <div key={`${row.source_id || index}`} className="customer-portal__mini-row">
                    <span>{index + 1}</span>
                    <strong>{dt(row.transaction_datetime)}</strong>
                    <b>{money(row.debit || row.credit || 0)}</b>
                    <p>{safeText(row.comment)}</p>
                  </div>
                ))}
                {!visibleTransactions.length && (
                  <div className="customer-portal__empty">No recent movements for this period.</div>
                )}
              </div>
            </section>
          </div>
        )}

        {activeTab === "security" && (
          <section className="customer-portal__panel customer-portal__panel--full">
            <div className="customer-portal__panel-head customer-portal__panel-head--wrap">
              <div>
                <p className="customer-portal__kicker">Security</p>
                <h2>Change your password</h2>
                <p className="customer-portal__muted">
                  Use this to keep your customer account secure when you need to reset access.
                </p>
              </div>
            </div>
            <form className="customer-portal__password" onSubmit={changePassword}>
              <label>
                Current password
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                />
              </label>
              <label>
                New password
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                />
              </label>
              <label>
                Confirm new password
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                />
              </label>
              <button className="customer-portal__primary" type="submit" disabled={savingPassword}>
                <FaLock /> {savingPassword ? "Updating..." : "Update password"}
              </button>
            </form>
          </section>
        )}

        {activeTab === "support" && (
          <div className="customer-portal__grid customer-portal__support-grid">
            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Support</p>
                  <h2>Create a support case</h2>
                  <p className="customer-portal__muted">
                    You can also use the PAMPA button and say something like: I want support, passbook is not loading.
                  </p>
                </div>
              </div>

              <form className="customer-portal__support-form" onSubmit={createTicket}>
                <label>
                  Title
                  <input
                    type="text"
                    value={ticketForm.title}
                    onChange={(e) => setTicketForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Short issue title"
                  />
                </label>
                <label>
                  Priority
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm((prev) => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label>
                <label>
                  Issue description
                  <textarea
                    rows={5}
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Tell us what happened, which screen, and what you expected."
                  />
                </label>
                <button className="customer-portal__primary" type="submit" disabled={savingTicket}>
                  <FaHeadset /> {savingTicket ? "Submitting..." : "Create case"}
                </button>
              </form>
            </section>

            <section className="customer-portal__panel">
              <div className="customer-portal__panel-head">
                <div>
                  <p className="customer-portal__kicker">Tracking</p>
                  <h2>Your support cases</h2>
                </div>
              </div>

              <div className="customer-portal__support-list">
                {loadingTickets ? (
                  <div className="customer-portal__empty">Loading your support cases...</div>
                ) : tickets.length ? (
                  tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      className={`customer-portal__support-ticket ${String(selectedTicketId) === String(ticket.id) ? "is-active" : ""}`}
                      onClick={() => setSelectedTicketId(ticket.id)}
                    >
                      <div>
                        <strong>{safeText(ticket.ticket_number)}</strong>
                        <span>{safeText(ticket.title)}</span>
                      </div>
                      <div className="customer-portal__support-ticket-meta">
                        <b>{safeText(ticket.status)}</b>
                        <small>{moment(ticket.created_at).format("DD MMM, hh:mm A")}</small>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="customer-portal__empty">No support cases yet.</div>
                )}
              </div>
            </section>

            <section className="customer-portal__panel customer-portal__panel--wide customer-portal__panel--full">
              <div className="customer-portal__panel-head customer-portal__panel-head--wrap">
                <div>
                  <p className="customer-portal__kicker">Case detail</p>
                  <h2>{safeText(selectedTicketDetail?.ticket?.title, "Select a support case")}</h2>
                  <p className="customer-portal__muted">
                    {selectedTicketDetail?.ticket
                      ? `${safeText(selectedTicketDetail.ticket.ticket_number)} • ${safeText(selectedTicketDetail.ticket.status)} • ${safeText(selectedTicketDetail.ticket.priority)} priority`
                      : "Choose a case on the right to read updates and add a reply."}
                  </p>
                </div>
              </div>

              {selectedTicketDetail?.ticket ? (
                <>
                  <div className="customer-portal__support-timeline">
                    {selectedTicketDetail.updates?.map((update) => (
                      <article key={update.id} className="customer-portal__support-update">
                        <div className="customer-portal__support-update-head">
                          <strong>{safeText(update.author_name, "FuelFlow")}</strong>
                          <span>{safeText(update.author_role)}</span>
                          <small>{moment(update.created_at).format("DD MMM YYYY, hh:mm A")}</small>
                        </div>
                        <p>{safeText(update.message)}</p>
                      </article>
                    ))}
                  </div>

                  <form className="customer-portal__support-reply" onSubmit={sendTicketReply}>
                    <textarea
                      rows={4}
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      placeholder="Add more details or respond to support here."
                    />
                    <button
                      className="customer-portal__primary"
                      type="submit"
                      disabled={savingTicketReply || !ticketReply.trim()}
                    >
                      <FaArrowRight /> {savingTicketReply ? "Sending..." : "Send reply"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="customer-portal__empty">Pick a support case to view updates.</div>
              )}
            </section>
          </div>
        )}
      </div>

      <PampaChatWidget />
    </div>
  );
}
